import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LogoutIcon from '@mui/icons-material/Logout';
import GroupsIcon from '@mui/icons-material/Groups';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { api, TrainingSessionSummary } from '../../services/api';
import { getAttendanceCoordinatesWithRetry } from '../../../common/utils/attendanceGeolocation';
import SessionFormDialog from './SessionFormDialog';

/**
 * Sessions manager used by the Event Overview Sessions section:
 * full CRUD plus per-session runtime actions (show clock-in /
 * clock-out QR, view attendee list, export attendees).
 *
 * The legacy standalone "Manage Sessions" tab (which mounted this
 * panel with `mode='manage'`) has been removed; the `mode` prop is
 * retained in case CRUD-only usage is reintroduced elsewhere.
 *
 * `mode='manage'`  → full CRUD, no runtime actions (QR / attendees).
 * `mode='overview'` → CRUD + runtime actions.
 */

export interface SessionsPanelProps {
  eventId: string;
  eventStartDate: string;
  eventEndDate: string;
  mode: 'manage' | 'overview';
  /**
   * When true, hide Add Session and the Edit/Delete Actions column while
   * keeping Clock-in / Clock-out QR, Attendees, and Export.
   */
  readOnly?: boolean;
  /**
   * When false, hide Clock-in / Clock-out QR actions.
   * Defaults to true (HIP and Ad-hoc both support session QR).
   */
  enableAttendanceQr?: boolean;
  onShowQr?: (session: TrainingSessionSummary, mode: 'clockIn' | 'clockOut') => void;
  onShowAttendees?: (session: TrainingSessionSummary) => void;
}

function formatDate(d: string): string {
  if (!d) return '—';
  return d.slice(0, 10);
}

function formatTime(t: string): string {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t || ''));
  if (!m) return t || '—';
  return `${m[1].padStart(2, '0')}:${m[2]}`;
}

function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-SG', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Open to the side and ignore pointer events so tooltips do not block stacked buttons. */
const SESSION_ACTION_TOOLTIP_PROPS = {
  arrow: true,
  placement: 'left' as const,
  PopperProps: {
    sx: { pointerEvents: 'none' },
  },
};

const NON_BLOCKING_TOOLTIP_POPPER_PROPS = {
  PopperProps: {
    sx: { pointerEvents: 'none' },
  },
};

function escapeCsv(value: string): string {
  const s = String(value ?? '');
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadTableData(
  format: 'csv' | 'excel',
  filename: string,
  headers: string[],
  rows: string[][],
) {
  let blob: Blob;
  let finalFilename = filename;
  if (format === 'csv') {
    const csv = [headers, ...rows].map((r) => r.map(escapeCsv).join(',')).join('\n');
    blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    finalFilename += '.csv';
  } else {
    const escapedHeaders = headers.map((h) => String(h ?? '').replace(/</g, '&lt;'));
    const escapedRows = rows.map((r) => r.map((v) => String(v ?? '').replace(/</g, '&lt;')));
    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta http-equiv="content-type" content="text/plain; charset=UTF-8"/></head>
      <body>
        <table border="1">
          <thead><tr style="background-color:#f2f2f2;font-weight:bold;">${escapedHeaders.map((h) => `<th>${h}</th>`).join('')}</tr></thead>
          <tbody>${escapedRows.map((r) => `<tr>${r.map((v) => `<td>${v}</td>`).join('')}</tr>`).join('')}</tbody>
        </table>
      </body></html>`;
    blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    finalFilename += '.xls';
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', finalFilename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

async function recordSessionQrHostLocation(sessionId: number): Promise<void> {
  try {
    const coords = await getAttendanceCoordinatesWithRetry();
    await api.updateSessionQrHostLocation(sessionId, coords);
  } catch {
    // Location is best-effort; the QR dialog can still open without it.
  }
}

const SessionsPanel: React.FC<SessionsPanelProps> = ({
  eventId,
  eventStartDate,
  eventEndDate,
  mode,
  readOnly = false,
  enableAttendanceQr = true,
  onShowQr,
  onShowAttendees,
}) => {
  const [sessions, setSessions] = useState<TrainingSessionSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState<boolean>(false);
  const [editingSession, setEditingSession] = useState<TrainingSessionSummary | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [exportMenu, setExportMenu] = useState<{ sessionId: number; anchor: HTMLElement } | null>(
    null,
  );
  const [exportingSessionId, setExportingSessionId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!eventId) {
      setSessions([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await api.listEventSessions(eventId);
      setSessions(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleAdd = () => {
    setEditingSession(null);
    setFormOpen(true);
  };

  const handleEdit = (session: TrainingSessionSummary) => {
    setEditingSession(session);
    setFormOpen(true);
  };

  const handleDelete = async (session: TrainingSessionSummary) => {
    if (session.hasAttendance) return;
    const confirmed = window.confirm(
      `Delete session "${session.name}" on ${formatDate(session.sessionDate)}?`,
    );
    if (!confirmed) return;
    setDeletingId(session.id);
    setError(null);
    try {
      await api.deleteEventSession(eventId, session.id);
      setSessions((prev) => prev.filter((s) => s.id !== session.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete session');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSaved = (saved: TrainingSessionSummary) => {
    setSessions((prev) => {
      const idx = prev.findIndex((s) => s.id === saved.id);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = saved;
        return next;
      }
      return [...prev, saved].sort((a, b) => {
        if (a.sessionDate !== b.sessionDate) return a.sessionDate.localeCompare(b.sessionDate);
        return a.startTime.localeCompare(b.startTime);
      });
    });
  };

  const disabledReason = (session: TrainingSessionSummary) =>
    session.hasAttendance
      ? `Cannot modify — ${session.attendeeCount} attendance record${session.attendeeCount === 1 ? '' : 's'} already tied to this session`
      : '';

  const showActionsColumn = mode === 'overview';
  const showCrudColumn = !readOnly;

  const emptyLabel = useMemo(
    () =>
      readOnly
        ? 'No sessions for this event yet.'
        : mode === 'manage'
          ? 'No sessions yet. Click "Add Session" to create one.'
          : 'No sessions for this event yet.',
    [mode, readOnly],
  );

  const exportTargetSession = exportMenu
    ? sessions.find((s) => s.id === exportMenu.sessionId) ?? null
    : null;

  const handleExport = async (format: 'csv' | 'excel') => {
    if (!exportTargetSession) return;
    const { id: sessionId, eventId: sessionEventId } = exportTargetSession;
    setExportMenu(null);
    setExportingSessionId(sessionId);
    setError(null);
    try {
      const { members } = await api.getSessionAttendedMembers(sessionId);
      if (members.length === 0) return;
      const headers = ['Name', 'CEA Number', 'Phone Number', 'Clocked In', 'Clocked Out'];
      const rows = members.map((m) => [
        m.name || '-',
        m.ceaNumber || '-',
        m.mobile || '-',
        formatTimestamp(m.clockedInAt || m.timeScanned),
        formatTimestamp(m.clockedOutAt),
      ]);
      const safeEventId = (sessionEventId || eventId || 'event').replace(/[^a-z0-9]/gi, '_');
      downloadTableData(format, `${safeEventId}_${sessionId}_attendees`, headers, rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to export attendees');
    } finally {
      setExportingSessionId(null);
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
          Sessions
        </Typography>
        {!readOnly && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={!eventId || loading}
          >
            Add Session
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && sessions.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      ) : sessions.length === 0 ? (
        <Box
          sx={{
            p: 2,
            border: '1px dashed rgba(var(--color-text-rgb), 0.15)',
            borderRadius: 1,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {emptyLabel}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">Name</TableCell>
                <TableCell align="center">Date</TableCell>
                <TableCell align="center">Start</TableCell>
                <TableCell align="center">End</TableCell>
                {showActionsColumn && <TableCell align="center">Session actions</TableCell>}
                {showCrudColumn && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((s) => {
                const reason = disabledReason(s);
                const editBtn = (
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(s)}
                      disabled={Boolean(reason) || deletingId === s.id}
                      aria-label="Edit session"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </span>
                );
                const deleteBtn = (
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => void handleDelete(s)}
                      disabled={Boolean(reason) || deletingId === s.id}
                      aria-label="Delete session"
                    >
                      {deletingId === s.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <DeleteIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                );
                return (
                  <TableRow key={s.id}>
                    <TableCell align="center">{s.name}</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      {formatDate(s.sessionDate)}
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      {formatTime(s.startTime)}
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      {formatTime(s.endTime)}
                    </TableCell>
                    {showActionsColumn && (
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Stack spacing={0.75} sx={{ alignItems: 'center' }}>
                          {enableAttendanceQr && (
                            <Stack
                              direction="row"
                              spacing={0.75}
                              sx={{ justifyContent: 'center', flexWrap: 'nowrap' }}
                            >
                              <Tooltip
                                title="Show the rotating QR for attendees to scan in"
                                {...SESSION_ACTION_TOOLTIP_PROPS}
                              >
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  startIcon={<QrCode2Icon />}
                                  onClick={() => {
                                    if (!onShowQr) return;
                                    void recordSessionQrHostLocation(s.id).finally(() =>
                                      onShowQr(s, 'clockIn'),
                                    );
                                  }}
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 1.25,
                                    py: 0.25,
                                    minWidth: 0,
                                  }}
                                >
                                  Clock-in QR
                                </Button>
                              </Tooltip>
                              <Tooltip
                                title="Show the rotating QR for attendees to scan out"
                                {...SESSION_ACTION_TOOLTIP_PROPS}
                              >
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  startIcon={<LogoutIcon />}
                                  onClick={() => {
                                    if (!onShowQr) return;
                                    void recordSessionQrHostLocation(s.id).finally(() =>
                                      onShowQr(s, 'clockOut'),
                                    );
                                  }}
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 1.25,
                                    py: 0.25,
                                    minWidth: 0,
                                  }}
                                >
                                  Clock-out QR
                                </Button>
                              </Tooltip>
                            </Stack>
                          )}
                          <Stack
                            direction="row"
                            spacing={0.75}
                            sx={{ justifyContent: 'center', flexWrap: 'nowrap' }}
                          >
                            <Tooltip
                              title="View the list of attendees for this session"
                              {...SESSION_ACTION_TOOLTIP_PROPS}
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<GroupsIcon />}
                                onClick={() => onShowAttendees && onShowAttendees(s)}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  px: 1.25,
                                  py: 0.25,
                                  minWidth: 0,
                                }}
                              >
                                Attendees
                              </Button>
                            </Tooltip>
                            <Tooltip
                              title="Download the attendee list as CSV or Excel"
                              {...SESSION_ACTION_TOOLTIP_PROPS}
                            >
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={
                                    exportingSessionId === s.id ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <FileDownloadIcon />
                                    )
                                  }
                                  endIcon={<KeyboardArrowDownIcon />}
                                  onClick={(e) =>
                                    setExportMenu({ sessionId: s.id, anchor: e.currentTarget })
                                  }
                                  disabled={s.attendeeCount === 0 || exportingSessionId === s.id}
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 1.25,
                                    py: 0.25,
                                    minWidth: 0,
                                  }}
                                >
                                  Export
                                </Button>
                              </span>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </TableCell>
                    )}
                    {showCrudColumn && (
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'center' }}>
                          {reason ? (
                            <Tooltip title={reason} arrow {...NON_BLOCKING_TOOLTIP_POPPER_PROPS}>
                              {editBtn}
                            </Tooltip>
                          ) : (
                            editBtn
                          )}
                          {reason ? (
                            <Tooltip title={reason} arrow {...NON_BLOCKING_TOOLTIP_POPPER_PROPS}>
                              {deleteBtn}
                            </Tooltip>
                          ) : (
                            deleteBtn
                          )}
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu
        anchorEl={exportMenu?.anchor ?? null}
        open={Boolean(exportMenu)}
        onClose={() => setExportMenu(null)}
      >
        <MenuItem onClick={() => void handleExport('csv')}>Export as CSV</MenuItem>
        <MenuItem onClick={() => void handleExport('excel')}>Export as Excel</MenuItem>
      </Menu>

      <SessionFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        eventId={eventId}
        eventStartDate={eventStartDate}
        eventEndDate={eventEndDate}
        session={editingSession}
        onSaved={handleSaved}
      />
    </Box>
  );
};

export default SessionsPanel;
